import { useEffect } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';

import { getSocket } from '@lib/socket';
import { useAppShellStore } from '@store/appShellStore';

/**
 * Refetch the given React Query keys whenever the backend emits
 * "<resource>:changed" for the current scope (any user created/edited/cancelled),
 * and once on (re)connect. Invalidation is unconditional — the acting client's
 * refetch just comes back ETag-304, and this way it also works across multiple
 * tabs of the same user. Per-centre resources (banks/courses/centres) attach a
 * `centreId` to the event payload — skip refetching when it names a centre
 * other than the one currently in view, since that data can't have changed here.
 *
 * Depends on centre/session so it re-attaches to the socket instance that
 * connectSocket() recreates when the scope changes.
 */
export function useRealtimeInvalidate(resources: string | string[], keys: QueryKey[]) {
  const queryClient = useQueryClient();
  const { centreId, sessionId } = useAppShellStore();

  const names = Array.isArray(resources) ? resources : [resources];
  const namesKey = names.join(',');
  const keysKey = JSON.stringify(keys);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const invalidate = (payload?: { centreId?: string }) => {
      if (payload?.centreId != null && payload.centreId !== centreId) return;
      keys.forEach((key) => queryClient.invalidateQueries({ queryKey: key }));
    };

    names.forEach((n) => socket.on(`${n}:changed`, invalidate));
    socket.on('connect', invalidate);

    return () => {
      names.forEach((n) => socket.off(`${n}:changed`, invalidate));
      socket.off('connect', invalidate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [namesKey, keysKey, centreId, sessionId, queryClient]);
}
